import {
  BriefcaseIcon,
  DevicePhoneMobileIcon,
  IdentificationIcon,
  StarIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import { useEffect, useId, useRef, useState } from 'react';
import {
  DUITNOW_ID_OPTIONS,
  type DuitNowIdType,
  type DuitNowSelection
} from '../../../../constants/duitnow.const';
import { validateDuitNowId } from '../../../../utils/duitnow-id';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../../../../components/ui/dialog';
import { OutlinedField } from './OutlinedField';
import { PaymentStepLayout } from './PaymentStepLayout';
import { PhoneNumberInput } from './PhoneNumberInput';
import { SelectableOptionCard } from './SelectableOptionCard';

type IdSelection = Extract<DuitNowSelection, { duitnow: 'id' }>;

interface Props {
  initial?: IdSelection;
  onConfirm: (s: IdSelection) => void;
  onBack: () => void;
  isLoading?: boolean;
  noTopMargin?: boolean;
  /** A /pay failure for this flow (invalid ID format, C1/C3 → backend message) is shown
   * here, inline, instead of leaving this screen — the typed value is kept either way. */
  serverError?: string;
}

const ICONS = {
  phone: <DevicePhoneMobileIcon className="h-6 w-6" />,
  user: <UserCircleIcon className="h-6 w-6" />,
  id: <IdentificationIcon className="h-6 w-6" />,
  briefcase: <BriefcaseIcon className="h-6 w-6" />,
  star: <StarIcon className="h-6 w-6" />
};

export function SelectDuitNowIdStep({
  initial,
  onConfirm,
  onBack,
  isLoading,
  noTopMargin,
  serverError
}: Readonly<Props>) {
  const [idType, setIdType] = useState<DuitNowIdType>(initial?.idType ?? '05');
  const [value, setValue] = useState<Record<DuitNowIdType, string>>({
    '01': '',
    '02': '',
    '03': '',
    '04': '',
    '05': '',
    ...(initial ? { [initial.idType]: initial.idValue } : {})
  });
  const [countryCode, setCountryCode] = useState<string | undefined>(
    initial?.countryCode ?? '60'
  );
  const [error, setError] = useState<string | null>(null);
  // A serverError must disappear once the user acts on it (edits the value or switches ID
  // type) — otherwise a stale message sits there through their next attempt. Re-arms (goes back
  // to "not dismissed") whenever a NEW serverError arrives, so a fresh failure always shows.
  const [serverErrorDismissed, setServerErrorDismissed] = useState(false);
  useEffect(() => {
    if (serverError) setServerErrorDismissed(false);
  }, [serverError]);
  const dismissServerError = () => setServerErrorDismissed(true);

  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const phoneInputId = useId();
  const option = DUITNOW_ID_OPTIONS.find((o) => o.type === idType)!;
  // Local (client-side) validation always takes priority — it's the freshest signal, and a
  // server error can only exist alongside it before the next submit attempt.
  const displayError =
    error ?? (serverErrorDismissed ? null : serverError) ?? null;

  const submit = () => {
    const err = validateDuitNowId(idType, value[idType]);
    setError(err);
    if (!err) {
      onConfirm({
        duitnow: 'id',
        idType,
        idValue: value[idType].trim(),
        countryCode: idType === '05' ? countryCode : undefined
      });
    }
  };

  // Back never validates, but an empty field is likely a miss (it can be below
  // the fold) rather than an intentional "no DuitNow ID" — so it confirms instead of
  // leaving silently. A filled field leaves immediately, as before.
  const handleBack = () => {
    if (value[idType].trim()) {
      onBack();
      return;
    }
    setConfirmLeaveOpen(true);
  };

  // Primary choice, and the same as Escape / outside-click / the dialog's own close (safe default).
  const stayAndFill = () => {
    setConfirmLeaveOpen(false);
    setTimeout(() => {
      fieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      fieldRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    }, 0);
  };

  const leaveAnyway = () => {
    setConfirmLeaveOpen(false);
    onBack();
  };

  return (
    <>
      <PaymentStepLayout
        backLabel="Authorization Method"
        onBack={handleBack}
        title="Select DuitNow ID"
        titleId={titleId}
        subtitle="We'll send an AutoDebit approval request to this account."
        continueDisabled={!value[idType].trim()}
        onContinue={submit}
        isLoading={isLoading}
        bottomBackLabel="Go back to select Authorization Method"
        noTopMargin={noTopMargin}
      >
        <div role="radiogroup" aria-labelledby={titleId} className="contents">
          {DUITNOW_ID_OPTIONS.map((o) => (
            <SelectableOptionCard
              key={o.type}
              icon={ICONS[o.icon]}
              title={o.label}
              selected={idType === o.type}
              onSelect={() => {
                setIdType(o.type);
                setError(null);
                dismissServerError();
              }}
              compact
            />
          ))}
        </div>
        <h2 className="mt-2 text-lg font-semibold text-[#0F172A]">
          DuitNow ID
        </h2>
        <div ref={fieldRef}>
          {idType === '05' ? (
            <div>
              <label
                htmlFor={phoneInputId}
                className="mb-1 block text-sm font-semibold text-[#0F172A]"
              >
                {option.fieldLabel} <span className="text-red-500">*</span>
              </label>
              {/* The register PhoneNumberInput pre-fills +60, so the design's placeholder cannot show; the label is rendered here. */}
              <PhoneNumberInput
                id={phoneInputId}
                setPhoneNumber={(v) => {
                  dismissServerError();
                  setValue((s) => ({
                    ...s,
                    '05':
                      (typeof v === 'function'
                        ? (
                            v as (
                              prev: string | undefined
                            ) => string | undefined
                          )(s['05'])
                        : v) ?? ''
                  }));
                }}
                setCountryPhoneCode={(c) =>
                  setCountryCode((prev) =>
                    typeof c === 'function'
                      ? (c as (p: string | undefined) => string | undefined)(
                          prev
                        )
                      : c
                  )
                }
                initialPhoneNumber={`${countryCode ?? ''}${value['05']}`}
                initialCountryCode={countryCode}
              />
              {displayError && (
                <p className="mt-1 text-sm text-red-500">{displayError}</p>
              )}
            </div>
          ) : (
            <OutlinedField
              id={`duitnow-id-${idType}`}
              label={option.fieldLabel}
              required
              value={value[idType]}
              placeholder={option.placeholder}
              inputMode="text"
              error={displayError ?? undefined}
              onChange={(v) => {
                dismissServerError();
                setValue((s) => ({ ...s, [idType]: v }));
              }}
            />
          )}
        </div>
      </PaymentStepLayout>

      <Dialog
        open={confirmLeaveOpen}
        onOpenChange={(open) => {
          if (!open) stayAndFill();
        }}
      >
        <DialogContent className="max-w-[22rem] rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900">
              Leave without adding your DuitNow ID?
            </DialogTitle>
            <DialogDescription>Your DuitNow ID is empty.</DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex flex-col gap-3">
            <button
              type="button"
              onClick={stayAndFill}
              className="w-full rounded-full bg-[#5CB891] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#4aa07c]"
            >
              Stay and fill
            </button>
            <button
              type="button"
              onClick={leaveAnyway}
              className="w-full rounded-full border-2 border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
            >
              Go back
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
